// Trava do boleto avulso da Gran: a UC não pode ter outra cobrança no mesmo mês.
//
// Chamado #6000 (24/09/2026): a UC 152499501254, da Administrar Condomínios, recebe
// a energia da usina da Gran, mas a conta de AGO/2026 entrou na fila da Vigor e saiu
// boleto no Sicoob (nº 5777). O boleto avulso da Gran não olhava isso: emitir por
// cima deixaria o cliente com dois boletos pela mesma energia, e a régua da Vigor
// continuaria cobrando o primeiro.
//
// O status vem de `derivarStatusBoleto`, a mesma regra da inadimplência.
//
// O outro lado também vale: a Vigor não calcula nem emite para UC marcada como
// cliente próprio da Gran (`unidades_consumidoras.fora_parceria_vigor`), nem por cima
// de um avulso da Gran vivo no mesmo mês. O banco repete a trava da marca no cálculo
// (migration 20260925143833); a do avulso vivo só existe aqui.
//
// A parte pura roda em `node --test`:
//   node --experimental-strip-types --test src/lib/__tests__/cobrancaNoMesmoMes.test.ts

import type { SupabaseClient } from '@supabase/supabase-js';
import { derivarStatusBoleto, type DadosStatusBoleto, type StatusBoleto } from './statusBoleto.ts';
import { normalizeUcCodigo } from './ucCodigo.ts';
import { normalizeMesReferencia, isMesReferenciaCanonico } from './mesReferencia.ts';

export interface FaturaDoMes extends DadosStatusBoleto {
  id: number;
  num_cobranca_boleto?: string | number | null;
}

/** Cobrança que não vale mais: não impede a nova. */
const SEM_EFEITO: StatusBoleto[] = ['Cancelado', 'Sem cobrança', 'Sem injeção'];

/** Cobrança que já recebeu dinheiro (ou virou acordo): cancelar não resolve. */
const JA_RESOLVIDA: Partial<Record<StatusBoleto, string>> = {
  Pago: 'já foi paga',
  Quitado: 'já foi quitada',
  Parcelado: 'já entrou num acordo de parcelamento',
};

/**
 * Por que não dá para emitir, ou `null` quando pode.
 *
 * `faturas` são as da mesma UC e do mesmo mês, tirando os avulsos da Gran fora da
 * parceria (esses a tela já pega como retentativa).
 */
export function motivoCobrancaNoMesmoMes(
  faturas: FaturaDoMes[],
  mes: string,
  hoje: Date = new Date(),
): string | null {
  for (const f of faturas) {
    const status = derivarStatusBoleto(f, hoje);
    if (SEM_EFEITO.includes(status)) continue;

    const qual = `fatura ${f.id}${f.num_cobranca_boleto ? `, boleto ${f.num_cobranca_boleto}` : ''}`;
    const resolvida = JA_RESOLVIDA[status];
    if (resolvida) {
      return `A cobrança desta UC em ${mes} ${resolvida} (${qual}). Fale com o Financeiro antes de cobrar de novo.`;
    }
    return `Esta UC já tem cobrança ${status === 'Vencido' ? 'vencida' : 'em aberto'} em ${mes} (${qual}). `
      + 'Cancele essa cobrança na tela de Faturas antes de emitir aqui: senão o cliente recebe dois boletos pela mesma energia.';
  }
  return null;
}

/** A UC é de cliente próprio da Gran? Então a Vigor não cobra. */
export function motivoUcForaParceria(
  uc: { fora_parceria_vigor?: boolean | null; numero_uc?: string | null } | null | undefined,
): string | null {
  if (!uc?.fora_parceria_vigor) return null;
  return `A UC ${uc.numero_uc ?? ''} é de cliente próprio da Gran, fora da parceria com a Vigor. `
    + 'A cobrança dela sai pelo Boleto avulso da Gran (Faturas → Nova ação), não por aqui.';
}

const CAMPOS_STATUS = 'id, num_cobranca_boleto, status_fatura, status_boleto_vigor, valor_boleto_vigor, '
  + 'valor_emissao_boleto_unico, vencimento_boleto, data_vencimento, data_pgto_boleto, quitado_em, '
  + 'energia_injetada_gd1_kwh, energia_injetada_gd2_kwh';

/**
 * Cobrança da UC no mês, de um lado só: `foraParceria` true olha os avulsos da Gran
 * fora da parceria; false olha o resto (Vigor e parceria). Erro de leitura sobe:
 * sem saber, quem chama não deixa emitir.
 */
export async function conferirCobrancaNoMes(
  supabase: SupabaseClient,
  ucCodigo: string,
  mesReferencia: string,
  foraParceria: boolean,
): Promise<string | null> {
  const uc = normalizeUcCodigo(ucCodigo);
  const mes = normalizeMesReferencia(mesReferencia);
  if (!uc || !isMesReferenciaCanonico(mes)) return null;
  const { data, error } = await supabase
    .from('faturas')
    .select(CAMPOS_STATUS)
    .eq('uc_codigo_norm', uc)
    .eq('mes_referencia', mes)
    .eq('fora_parceria_vigor', foraParceria)
    .eq('ambiente', 'producao');
  if (error) throw new Error('Não consegui conferir se esta UC já tem cobrança no mês: ' + error.message);
  return motivoCobrancaNoMesmoMes((data || []) as unknown as FaturaDoMes[], mes);
}

/**
 * Por que a Vigor não pode cobrar esta UC neste mês, ou `null`. Vale para o cálculo e
 * para a emissão de fatura comum (nunca para o avulso da Gran).
 */
export async function bloqueioCobrancaVigor(
  supabase: SupabaseClient,
  ucCodigo: string,
  mesReferencia: string,
): Promise<string | null> {
  if (!ucCodigo) return null;
  // `resolver_uc_por_codigo` devolve a UC inteira (casa exato ou normalizado, nos dois
  // números) — a mesma resolução do cálculo e da emissão.
  const { data, error } = await supabase.rpc('resolver_uc_por_codigo', { p_codigo: ucCodigo });
  if (error) throw new Error('Não consegui conferir o cadastro da UC: ' + error.message);
  const uc = (Array.isArray(data) ? data[0] : data) as { fora_parceria_vigor?: boolean | null; numero_uc?: string | null } | null;
  return motivoUcForaParceria(uc) ?? conferirCobrancaNoMes(supabase, ucCodigo, mesReferencia, true);
}
